<template>
  <div class="max-w-7xl mx-auto pb-10 relative space-y-6">

    <!-- Encabezado -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in" :class="{ 'blur-sm pointer-events-none opacity-50': showModal || showWizard }">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
          <Bot class="w-6 h-6 text-indigo-500" />
          Mis Agentes de IA
        </h1>
        <p class="text-slate-500 dark:text-slate-400 text-sm mt-1">
          Gestiona tus asistentes virtuales y conéctalos a WhatsApp.
        </p>
      </div>

      <!-- BOTÓN PARA ABRIR EL WIZARD MÁGICO (CON VALIDACIÓN DE PLAN) -->
      <button
        @click="checkPlanAndCreate"
        class="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-600/30 transition-all active:scale-95"
      >
        <Sparkles class="w-5 h-5" /> Crear Agente Mágico
      </button>
    </div>

    <!-- ... (BARRA DE BÚSQUEDA Y GRID DE AGENTES SE MANTIENEN IGUAL) ... -->
    <!-- Barra de Filtros -->
    <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4 animate-fade-in" :class="{ 'blur-sm pointer-events-none opacity-50': showModal || showWizard }">
      <div class="relative flex-1">
        <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          v-model="searchTerm"
          type="text"
          placeholder="Buscar agente por nombre..."
          class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-slate-700 dark:text-slate-200"
        >
      </div>
    </div>

    <!-- Carga y Estado Vacío -->
    <div v-if="isLoading" class="flex justify-center items-center py-20">
      <Loader2 class="w-8 h-8 text-indigo-500 animate-spin" />
    </div>

    <div v-else-if="mappedAgents.length === 0" class="text-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
      <Bot class="w-12 h-12 text-slate-300 mx-auto mb-3" />
      <h3 class="text-lg font-bold text-slate-700 dark:text-slate-300">Sin agentes configurados</h3>
      <p class="text-sm text-slate-500">Crea tu primer asistente de inteligencia artificial.</p>
    </div>

    <!-- Grid de Agentes -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" :class="{ 'blur-sm pointer-events-none opacity-50': showModal || showWizard }">
      <div v-for="agent in mappedAgents" :key="agent.id" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-lg transition-all overflow-hidden group flex flex-col">
        <!-- Header con Foto -->
        <div class="h-24 bg-gradient-to-r from-indigo-500 to-purple-600 relative">
          <div class="absolute -bottom-10 left-4">
            <img :src="agent.photo_url || `https://ui-avatars.com/api/?name=${agent.name}&background=1e1b4b&color=fff`" class="w-20 h-20 rounded-2xl object-cover border-4 border-white dark:border-slate-900 shadow-md bg-white dark:bg-slate-800" />
          </div>
          <span class="absolute top-4 right-4 px-2 py-1 text-[10px] font-bold uppercase rounded-md shadow-sm" :class="agent.state == 1 ? 'bg-white text-indigo-600' : 'bg-slate-800 text-slate-300'">
            {{ agent.state == 1 ? 'Activo' : 'Pausado' }}
          </span>
        </div>

        <!-- Info del Agente -->
        <div class="pt-12 px-5 pb-5 flex-1 flex flex-col">
          <h3 class="text-lg font-bold text-slate-900 dark:text-white line-clamp-1">{{ agent.name }}</h3>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2 min-h-[32px]">{{ agent.bio || 'Sin descripción' }}</p>

          <div class="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex-1">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Conexión Evolution API</p>
            <div v-if="agent.instance_name" class="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-slate-100 dark:border-slate-700/50">
              <span class="relative flex h-3 w-3">
                <span v-if="agent.evoStatus === 'open'" class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-3 w-3" :class="agent.evoStatus === 'open' ? 'bg-emerald-500' : 'bg-red-500'"></span>
              </span>
              <div class="flex-1 overflow-hidden">
                <p class="text-xs font-bold text-slate-700 dark:text-slate-300 truncate">{{ agent.instance_name }}</p>
                <p class="text-[10px] text-slate-500 truncate">{{ agent.evoNumber || 'Sin número' }}</p>
              </div>
            </div>
            <div v-else class="flex items-center gap-2 text-xs text-orange-500 bg-orange-50 dark:bg-orange-500/10 p-2 rounded-lg">
              <AlertTriangle class="w-4 h-4" /> Sin Instancia Asignada
            </div>
          </div>

          <!-- Acciones -->
          <div class="mt-5 flex gap-2">
            <button @click="openEdit(agent)" class="flex-1 flex items-center justify-center gap-2 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl transition-colors">
              <Settings2 class="w-4 h-4" /> Configurar
            </button>
            <button @click="deleteItem(agent)" class="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-colors">
              <Trash2 class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODALES -->

    <!-- Formulario Clásico (Podemos borrarlo si el Wizard ya hace todo, pero lo dejo por si acaso) -->
    <AgentForm
      v-if="showModal"
      :show="showModal"
      :agentToEdit="selectedItem"
      :evolutionInstances="evolutionInstances"
      @close="showModal = false"
      @saved="loadData"
    />

    <!-- Wizard Mágico (Recibe los límites del plan) -->
    <WizardAgent
      v-if="showWizard"
      :show="showWizard"
      :evolutionInstances="evolutionInstances"
      :existingAgents="agentsList"
      :agentToEdit="selectedItem"
      :planLimits="currentPlan"
      @close="showWizard = false"
      @saved="loadData"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, Bot, Trash2, Settings2, Loader2, AlertTriangle, Sparkles } from 'lucide-vue-next';
import apiClient from '@/api/client';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';

// Importar Componentes
import AgentForm from './Form.vue';
import WizardAgent from './Wizard.vue';

const agentsList = ref([]);
const evolutionInstances = ref([]);
const currentPlan = ref(null); // <-- Almacena los límites de la membresía del usuario
const isLoading = ref(true);
const searchTerm = ref('');

const showModal = ref(false);
const showWizard = ref(false);
const selectedItem = ref(null);

const { showSuccess, showError } = useToast();

const loadData = async () => {
  isLoading.value = true;
  try {
    const currentUser = SecureStorage.getItem('neurals_user');
    const isAdmin = currentUser?.role_id !== 3;
    const queryParams = {};

    if (!isAdmin) {
      queryParams.owner = currentUser.id;

      // 1. Obtener los detalles del plan del usuario
      const planId = currentUser.is_premium > 0 ? currentUser.is_premium : 1;
      const resPlan = await apiClient.get(`/plans/${planId}`);
      currentPlan.value = resPlan.data;
    }

    // 2. CARGAR AGENTES (MySQL filtrado por rol)
    const resAgents = await apiClient.get('/ai_characters', { params: queryParams });
    agentsList.value = Array.isArray(resAgents.data) ? resAgents.data : (resAgents.data.data || []);

    // 3. CARGAR INSTANCIAS (MySQL filtrado por rol)
    const resDbInst = await apiClient.get('/instances', { params: queryParams });
    const userInstances = Array.isArray(resDbInst.data) ? resDbInst.data : (resDbInst.data.data || []);

    const evoBaseUrl = 'https://wapi.therealdevs.pro';
    const evoHeaders = { headers: { apikey: 'TheRealDevsPro2026' } };

    // 4. CRUCE CON EVOLUTION SEGÚN ROL
    if (isAdmin) {
      // ADMIN: 1 sola llamada global
      const resEvo = await axios.get(`${evoBaseUrl}/instance/fetchInstances`, evoHeaders);
      const allEvoData = resEvo.data || [];

      evolutionInstances.value = userInstances.map(dbInst => {
        const liveData = allEvoData.find(e => e.name === dbInst.name) || {};
        return {
          ...dbInst,
          connectionStatus: liveData.connectionStatus || 'close',
          ownerJid: liveData.ownerJid || null,
          profilePicUrl: liveData.profilePicUrl || null
        };
      });

    } else {
      // CLIENTE NORMAL: Consultas concurrentes individuales sin exponer otras instancias
      const promises = userInstances.map(async (dbInst) => {
        let connectionStatus = 'close';
        try {
          const res = await axios.get(`${evoBaseUrl}/instance/connectionState/${dbInst.name}`, evoHeaders);
          connectionStatus = res.data?.instance?.state || res.data?.state || 'close';
        } catch {
          connectionStatus = 'close';
        }

        return {
          ...dbInst,
          connectionStatus,
          ownerJid: null,
          profilePicUrl: null
        };
      });

      evolutionInstances.value = await Promise.all(promises);
    }

  } catch (e) {
    console.error(e);
    showError('Error cargando los datos del sistema.');
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

// VALIDACIÓN DE NEGOCIO: ¿Puede crear un nuevo Agente?
const checkPlanAndCreate = () => {
  // Verificamos si el usuario es cliente (Role 3) y si ya superó el límite de agentes permitidos
  if (currentPlan.value && agentsList.value.length >= currentPlan.value.max_instances) {
    Swal.fire({
      icon: 'info',
      title: 'Límite Alcanzado',
      text: `Tu plan actual (${currentPlan.value.name}) solo te permite tener un máximo de ${currentPlan.value.max_instances} Agentes/Instancias.`,
      confirmButtonText: 'Hacer Upgrade',
      confirmButtonColor: '#4f46e5',
      showCancelButton: true,
      cancelButtonText: 'Cerrar'
    }).then((result) => {
      if (result.isConfirmed) {
        // Redirigir a la vista de pagos / planes del cliente
        // router.push('/billing');
      }
    });
    return;
  }

  // Si no ha superado el límite, le abrimos el Wizard en blanco
  selectedItem.value = null;
  showWizard.value = true;
};

const mappedAgents = computed(() => {
  let filtered = agentsList.value;
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    filtered = filtered.filter(a => a.name?.toLowerCase().includes(term));
  }
  return filtered.map(agent => {
    const instanceData = evolutionInstances.value.find(inst => inst.name === agent.instance_name);
    return {
      ...agent,
      evoStatus: instanceData?.connectionStatus || 'offline',
      evoNumber: instanceData?.ownerJid?.split('@')[0] || null
    };
  });
});

const openEdit = (item) => {
  selectedItem.value = item;
  showWizard.value = true;
};

const deleteItem = (agent) => {
  // (La lógica de eliminación se mantiene igual)
  Swal.fire({
    title: '¿Eliminar Agente?',
    text: "Se borrará su configuración y el historial de chat asociado.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        await apiClient.delete(`/ai_characters/${agent.id}`);
        agentsList.value = agentsList.value.filter(a => a.id !== agent.id);
        showSuccess('Agente eliminado.');
      } catch (e) {
        showError('Error al eliminar el agente.');
      }
    }
  });
};
</script>